import {
  getAttendanceRecords as getAttendanceRecordsService,
  getTeamAttendanceRecords as getTeamAttendanceRecordsService,
  getMyAttendanceRecords as getMyAttendanceRecordsService,
  verifyAttendanceRecord as verifyAttendanceRecordService,
  getMonthlyAttendanceReport as getMonthlyAttendanceReportService,
  confirmAttendance,
  getLeaveCodes,
  getEmployeeLeaveReport,
  deleteAttendanceRecord as deleteAttendanceRecordService,
} from "services/company/attendanceManagement/attendanceRecords/attendanceRecordsService";

import {
  getAttendanceRecordsStart,
  getAttendanceRecordsSuccess,
  getAttendanceRecordsFailure,
  updateAttendanceApprovalStatus,
} from "./attendanceRecordsSlice";

import { showSuccessToast, showErrorToast } from "utils/toastNotification";

// Fetch attendance records
export const fetchAttendanceRecords =
  (params = {}) =>
  async (dispatch) => {
    try {
      dispatch(getAttendanceRecordsStart());

      const response = await getAttendanceRecordsService(params);

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(
          getAttendanceRecordsSuccess({
            data: response.data,
            pagination: response.pagination,
          }),
        );

        return {
          success: true,
          data: response.data,
          pagination: response.pagination,
        };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to fetch attendance records";

        dispatch(getAttendanceRecordsFailure(errorMsg));
        showErrorToast(errorMsg);

        return {
          success: false,
          error: errorMsg,
        };
      }
    } catch (error) {
      console.error("Fetch attendance records error:", error);

      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to fetch attendance records";

      dispatch(getAttendanceRecordsFailure(errorMsg));
      showErrorToast(errorMsg);

      return {
        success: false,
        error: errorMsg,
      };
    }
  };

// Fetch monthly attendance report
export const fetchMonthlyAttendanceReport =
  (params = {}) =>
  async (dispatch) => {
    try {
      dispatch(getAttendanceRecordsStart());

      const response = await getMonthlyAttendanceReportService(params);

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(
          getAttendanceRecordsSuccess({
            data: response.data.employees || [],
            pagination: {
              month: response.data.month,
              monthName: response.data.monthName,
              year: response.data.year,
              total_employees: response.data.employees?.length || 0,
            },
          }),
        );
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to fetch monthly attendance";
        dispatch(getAttendanceRecordsFailure(errorMsg));
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Fetch monthly attendance error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to fetch monthly attendance";
      dispatch(getAttendanceRecordsFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

// Fetch team attendance records (Reporting Manager)
export const fetchTeamAttendanceRecords =
  (params = {}) =>
  async (dispatch) => {
    try {
      dispatch(getAttendanceRecordsStart());

      const response = await getTeamAttendanceRecordsService(params);

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(
          getAttendanceRecordsSuccess({
            data: response.data,
            pagination: response.pagination,
          }),
        );

        return {
          success: true,
          data: response.data,
          pagination: response.pagination,
        };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to fetch team attendance records";

        dispatch(getAttendanceRecordsFailure(errorMsg));
        showErrorToast(errorMsg);

        return {
          success: false,
          error: errorMsg,
        };
      }
    } catch (error) {
      console.error("Fetch team attendance records error:", error);

      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to fetch team attendance records";

      dispatch(getAttendanceRecordsFailure(errorMsg));
      showErrorToast(errorMsg);

      return {
        success: false,
        error: errorMsg,
      };
    }
  };

// Verify Attendance Record (Approve / Reject)
export const verifyAttendance =
  (id, data = {}, currentParams = {}) =>
  async (dispatch) => {
    try {
      const response = await verifyAttendanceRecordService(id, data);

      if (response?.meta?.status === true || response?.meta?.code === 200) {
        showSuccessToast(
          response?.meta?.message ||
            `Attendance record ${
              data.approval_status || "verified"
            } successfully`,
        );
        dispatch(
          updateAttendanceApprovalStatus({
            id,
            approval_status: data.approval_status,
          }),
        );
        dispatch(fetchAttendanceRecords(currentParams));
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to verify attendance record";
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Verify attendance record error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to verify attendance record";
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

// Delete Attendance Record
export const deleteAttendance =
  (id, currentParams = {}) =>
  async (dispatch) => {
    try {
      const response = await deleteAttendanceRecordService(id);

      if (response?.meta?.status === true || response?.meta?.code === 200) {
        showSuccessToast(
          response?.meta?.message || "Attendance record deleted successfully",
        );
        // Refresh the attendance records list
        if (currentParams.viewMode === "monthly") {
          dispatch(fetchMonthlyAttendanceReport(currentParams));
        } else {
          dispatch(fetchAttendanceRecords(currentParams));
        }
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to delete attendance record";
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Delete attendance record error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to delete attendance record";
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

// Confirm attendance for payroll
export const confirmAttendanceForPayroll =
  (data, currentParams = {}) =>
  async (dispatch) => {
    try {
      const response = await confirmAttendance(data);

      if (response?.meta?.status === true || response?.meta?.code === 200) {
        showSuccessToast(
          response?.meta?.message ||
            "Attendance confirmed successfully for payroll",
        );
        // Refresh the monthly attendance data
        dispatch(fetchMonthlyAttendanceReport(currentParams));
        return { success: true, data: response.data };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to confirm attendance";
        showErrorToast(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("Confirm attendance error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to confirm attendance";
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

// Fetch my attendance records (Logged-in Employee)
export const fetchMyAttendanceRecords =
  (params = {}) =>
  async (dispatch) => {
    try {
      dispatch(getAttendanceRecordsStart());

      const response = await getMyAttendanceRecordsService(params);

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        dispatch(
          getAttendanceRecordsSuccess({
            data: response.data,
            pagination: response.pagination,
          }),
        );

        return {
          success: true,
          data: response.data,
          pagination: response.pagination,
        };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to fetch my attendance records";

        dispatch(getAttendanceRecordsFailure(errorMsg));
        showErrorToast(errorMsg);

        return {
          success: false,
          error: errorMsg,
        };
      }
    } catch (error) {
      console.error("Fetch my attendance records error:", error);

      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to fetch my attendance records";

      dispatch(getAttendanceRecordsFailure(errorMsg));
      showErrorToast(errorMsg);

      return {
        success: false,
        error: errorMsg,
      };
    }
  };

// Fetch codes for legend
export const fetchLeaveCodes = () => async (dispatch) => {
  try {
    const response = await getLeaveCodes();

    if (response?.meta?.status === true && response?.meta?.code === 200) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      const errorMsg = response?.meta?.message || "Failed to fetch leave codes";
      showErrorToast(errorMsg);
      return {
        success: false,
        error: errorMsg,
      };
    }
  } catch (error) {
    console.error("Fetch leave codes error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Failed to fetch leave codes";
    showErrorToast(errorMsg);
    return {
      success: false,
      error: errorMsg,
    };
  }
};

// Fetch employee leave report
export const fetchEmployeeLeaveReport =
  (params = {}) =>
  async (dispatch) => {
    try {
      const response = await getEmployeeLeaveReport(params);

      if (response?.meta?.status === true && response?.meta?.code === 200) {
        return {
          success: true,
          data: response.data,
          pagination: response.pagination,
        };
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to fetch employee leave report";
        showErrorToast(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }
    } catch (error) {
      console.error("Fetch employee leave report error:", error);
      const errorMsg =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Failed to fetch employee leave report";
      showErrorToast(errorMsg);
      return {
        success: false,
        error: errorMsg,
      };
    }
  };
